;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import React, { Children, isValidElement } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import SvgIcon from 'components/ui/SvgIcon';
import Text from 'components/ui/Text';

import { getEntryLink } from 'utils/getEntryLink';

import styles from './RichText.module.scss';

const propTypes = {
    children: PropTypes.node,
};

const NON_DESCRIPTIVE_LINK_TEXT = /^(click\s+)?here\.?$|^learn\s+more\.?$/i;

export const getNodeText = content => {
    if (!Array.isArray(content)) {
        return '';
    }

    return content
        .map(item => item?.value || getNodeText(item?.content))
        .join('')
        .trim();
};

export const getReadableDestination = href => {
    try {
        const url = new URL(href, 'https://www.tillamook.com');
        return [...url.pathname.split('/'), url.hash.replace('#', '')]
            .filter(Boolean)
            .join(' ')
            .replace(/-/g, ' ');
    } catch {
        return href?.replace(/[-/#]/g, ' ').trim();
    }
};

export const nodeContainsTableHeaderCell = node => {
    if (!node) {
        return false;
    }

    if (node.nodeType === 'table-header-cell') {
        return true;
    }

    return Array.isArray(node.content)
        ? node.content.some(nodeContainsTableHeaderCell)
        : false;
};

const isTableHeaderCell = node => node?.nodeType === 'table-header-cell';

const getTableHeaderScope = (rowNode, cellIndex) => {
    const cells = rowNode?.content || [];
    const headerCells = cells.filter(isTableHeaderCell);

    if (headerCells.length === 0) {
        return undefined;
    }

    if (headerCells.length === cells.length) {
        return 'col';
    }

    return cellIndex === 0 ? 'row' : 'col';
};

export const getAccessibleLinkLabel = (content, href, fallback = '') => {
    const text = getNodeText(content);
    const destination = fallback || getReadableDestination(href);

    if (!text) {
        return destination ? `Link to ${destination}` : undefined;
    }

    if (!NON_DESCRIPTIVE_LINK_TEXT.test(text)) {
        return undefined;
    }

    return destination ? `${text} about ${destination}` : undefined;
};

const getEntryLabel = entry => {
    return (
        entry?.ariaLabel ||
        entry?.displayName ||
        entry?.title ||
        entry?.name ||
        entry?.seoTitle ||
        ''
    );
};

export const getAccessibleEntryLinkLabel = (content, entry, fallback = '') => {
    const text = getNodeText(content);
    const href = entry ? getEntryLink(entry)?.href : '';
    const destination =
        fallback || getEntryLabel(entry) || getReadableDestination(href);

    if (text) {
        return getAccessibleLinkLabel(content, href, destination);
    }

    return destination ? `Link to ${destination}` : undefined;
};

// MARKS
export const Bold = ({ children = null }) => {
    return <b className={styles.bold}>{children}</b>;
};

export const Italic = ({ children = null }) => {
    return <i className={styles.italic}>{children}</i>;
};

export const Underline = ({ children = null }) => {
    return <u className={styles.underline}>{children}</u>;
};

Bold.propTypes = propTypes;

Italic.propTypes = propTypes;

Underline.propTypes = propTypes;

// BLOCKS
export const BodyText = ({ children = null }) => (
    <Text
        as="p"
        baseTheme="bodySmall"
        themes={{ large: 'bodyMedium' }}
        className={styles.paragraph}
        fixWidows
    >
        {children}
    </Text>
);

export const OrderedList = ({ children = null }) => (
    <ol className={styles.orderedList}>{children}</ol>
);

export const UnorderedList = ({ children = null }) => (
    <ul className={styles.unorderedList}>{children}</ul>
);

export const ListItem = ({ children = null }) => (
    <li className={styles.listItem}>{children}</li>
);

export const HeadingOne = ({ children = null }) => (
    <Text
        as="h1"
        baseTheme="displayLarge"
        className={cx(styles.heading, styles.largeHeading)}
    >
        {children}
    </Text>
);

export const HeadingTwo = ({ children = null }) => (
    <Text
        as="h2"
        baseTheme="headingXLarge"
        className={cx(styles.heading, styles.largeHeading)}
    >
        {children}
    </Text>
);

export const HeadingThree = ({ children = null }) => (
    <Text
        as="h3"
        baseTheme="headingLarge"
        className={cx(styles.heading, styles.largeHeading)}
    >
        {children}
    </Text>
);

export const HeadingFour = ({ children = null }) => (
    <Text as="h4" baseTheme="headingMedium" className={styles.heading}>
        {children}
    </Text>
);

export const HeadingFive = ({ children = null }) => (
    <Text as="h5" baseTheme="headingSmall" className={styles.heading}>
        {children}
    </Text>
);

export const HeadingSix = ({ children = null }) => (
    <Text as="h6" baseTheme="headingXSmall" className={styles.heading}>
        {children}
    </Text>
);

export const Blockquote = ({ children = null }) => (
    <Text as="blockquote" baseTheme="bodySmall" className={styles.blockquote}>
        <SvgIcon type="quote" />
        {children}
    </Text>
);

export const Table = ({ children = null, presentation = false }) => (
    <table
        className={styles.table}
        role={presentation ? 'presentation' : undefined}
    >
        <tbody>{children}</tbody>
    </table>
);

export const TableRow = ({ node, children = null }) => {
    const cells = Children.toArray(children).map((child, index) => {
        if (!isValidElement(child) || child.type !== 'th') {
            return child;
        }

        return React.cloneElement(child, {
            scope: getTableHeaderScope(node, index),
        });
    });

    return <tr>{cells}</tr>;
};

export const TableCell = ({ children = null }) => <td>{children}</td>;

export const TableHeaderCell = ({ children = null, scope }) => (
    <th scope={scope}>{children}</th>
);

BodyText.propTypes = propTypes;

OrderedList.propTypes = propTypes;

UnorderedList.propTypes = propTypes;

ListItem.propTypes = propTypes;

Blockquote.propTypes = propTypes;

Table.propTypes = {
    ...propTypes,
    presentation: PropTypes.bool,
};

TableRow.propTypes = {
    ...propTypes,
    node: PropTypes.object,
};

TableCell.propTypes = propTypes;

TableHeaderCell.propTypes = {
    ...propTypes,
    scope: PropTypes.oneOf(['col', 'row']),
};

HeadingOne.propTypes = propTypes;

HeadingTwo.propTypes = propTypes;

HeadingThree.propTypes = propTypes;

HeadingFour.propTypes = propTypes;

HeadingFive.propTypes = propTypes;

HeadingSix.propTypes = propTypes;
